import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge } from "../../../../ui/components/Badge";
import { Button } from "../../../../ui/components/Button";
import { Table } from "../../../../ui/components/Table";
import { TextField } from "../../../../ui/components/TextField";
import {
  FeatherAlertCircle,
  FeatherCheckCircle,
  FeatherExternalLink,
  FeatherKey,
  FeatherRefreshCw,
  FeatherUserPlus,
} from "@subframe/core";
import {
  ZohoUserDTO,
  ZohoUserTenantDTO,
  addZohoUser,
  getZohoUser,
  listZohoUsers,
  reconnectZohoUser,
  removeZohoUser,
  testZohoUser,
  updateZohoUser,
} from "../../data/api/botZohoApi";

/**
 * "Zoho Books Automation Accounts → Manage users".
 *
 * Zoho accounts the bot makes Books API calls as. Auth is Zoho's Self Client
 * flow: the admin generates a grant code in the API console of the account's
 * datacentre (api-console.zoho.in or api-console.zoho.com — it lives 3–10
 * minutes) and pastes it here together with that Self Client's id/secret.
 * Each user is tagged with its datacentre at creation; a bot can only be
 * invited into organisations on that DC, so US clients need a `.com` bot.
 * The backend exchanges it on submit, proves the token by listing the user's
 * Books orgs, and stores everything encrypted. Each user carries at most
 * max_tenants tenants — the assignment UI comes with the tenant-mapping phase.
 */

const SELF_CLIENT_SCOPE = "ZohoBooks.fullaccess.all";

/**
 * The datacentres the UI offers. Zoho has seven; the backend accepts all of
 * them, but only India and the US have clients today, and a select with five
 * never-used rows is where the wrong one gets picked.
 */
const DATACENTRES = [
  { value: "in", label: "India (.in)" },
  { value: "com", label: "US (.com)" },
] as const;

const apiConsoleHost = (dc: string) => `api-console.zoho.${dc || "in"}`;
const dcLabel = (dc: string) =>
  DATACENTRES.find((d) => d.value === dc)?.label ?? `.${dc}`;

const statusBadge = (user: ZohoUserDTO) => {
  if (user.status === "connected") return <Badge variant="success">Connected</Badge>;
  if (user.status === "error") return <Badge variant="error">Needs attention</Badge>;
  return <Badge variant="warning">Disconnected</Badge>;
};

const formatWhen = (iso: string | null): string => {
  if (!iso) return "never";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "never" : date.toLocaleString();
};

const EMPTY_ADD_FORM = {
  label: "",
  zoho_email: "",
  dc: "in",
  client_id: "",
  client_secret: "",
  grant_code: "",
};

export const BotZohoUsersPage: React.FC = () => {
  const [users, setUsers] = useState<ZohoUserDTO[]>([]);
  const [loading, setLoading] = useState(true);

  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ ...EMPTY_ADD_FORM });
  const [adding, setAdding] = useState(false);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [tenants, setTenants] = useState<ZohoUserTenantDTO[]>([]);
  const [testing, setTesting] = useState(false);

  const [showReconnect, setShowReconnect] = useState(false);
  const [reconnectCode, setReconnectCode] = useState("");
  const [reconnectSecret, setReconnectSecret] = useState("");
  const [reconnecting, setReconnecting] = useState(false);

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [savingLogin, setSavingLogin] = useState(false);

  const selected = useMemo(
    () => users.find((u) => u.public_id === selectedId) ?? null,
    [users, selectedId]
  );

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await listZohoUsers();
      setUsers(res.data.users);
    } catch (err: any) {
      setError(err.message || "Failed to load Zoho users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Selecting a user pulls its tenant assignments (orgs ride on the row).
  useEffect(() => {
    if (!selectedId) {
      setTenants([]);
      return;
    }
    setShowReconnect(false);
    setReconnectCode("");
    setReconnectSecret("");
    // Never carry a typed credential across a selection change.
    setLoginEmail("");
    setLoginPassword("");
    getZohoUser(selectedId)
      .then((res) => setTenants(res.data.tenants))
      .catch(() => setTenants([]));
  }, [selectedId]);

  const replaceUser = (user: ZohoUserDTO) =>
    setUsers((prev) => prev.map((u) => (u.public_id === user.public_id ? user : u)));

  const handleAdd = async () => {
    try {
      setAdding(true);
      setError(null);
      setNotice(null);
      const res = await addZohoUser({
        label: addForm.label.trim() || undefined,
        zoho_email: addForm.zoho_email.trim(),
        dc: addForm.dc,
        client_id: addForm.client_id.trim(),
        client_secret: addForm.client_secret.trim(),
        grant_code: addForm.grant_code.trim(),
      });
      const user = res.data.user;
      setNotice(
        `Connected ${user.zoho_email} — Zoho Books answered with ` +
          `${user.organizations.length} organisation(s).`
      );
      setAddForm({ ...EMPTY_ADD_FORM });
      setShowAdd(false);
      await fetchUsers();
      setSelectedId(user.public_id);
    } catch (err: any) {
      setError(err.message || "Could not add the Zoho user");
    } finally {
      setAdding(false);
    }
  };

  const handleTest = async (publicId: string) => {
    try {
      setTesting(true);
      setError(null);
      setNotice(null);
      const res = await testZohoUser(publicId);
      replaceUser(res.data.user);
      setNotice(
        `Zoho Books responded — ${res.data.organizations.length} organisation(s) reachable.`
      );
    } catch (err: any) {
      setError(err.message || "The connection test failed");
      // The backend records the failure on the row — reload to show it.
      await fetchUsers();
    } finally {
      setTesting(false);
    }
  };

  const handleReconnect = async () => {
    if (!selected) return;
    try {
      setReconnecting(true);
      setError(null);
      setNotice(null);
      const res = await reconnectZohoUser({
        public_id: selected.public_id,
        grant_code: reconnectCode.trim(),
        client_secret: reconnectSecret.trim() || undefined,
      });
      replaceUser(res.data.user);
      setShowReconnect(false);
      setReconnectCode("");
      setReconnectSecret("");
      setNotice(`Reconnected ${res.data.user.zoho_email}.`);
    } catch (err: any) {
      setError(err.message || "Reconnect failed");
    } finally {
      setReconnecting(false);
    }
  };

  const handleSaveLogin = async () => {
    if (!selected) return;
    try {
      setSavingLogin(true);
      setError(null);
      setNotice(null);
      const res = await updateZohoUser({
        public_id: selected.public_id,
        ...(loginEmail.trim() ? { zoho_login_email: loginEmail.trim() } : {}),
        ...(loginPassword ? { zoho_password: loginPassword } : {}),
      });
      replaceUser(res.data.user);
      // Drop the typed password from memory the moment it is stored.
      setLoginPassword("");
      setNotice(
        `Browser sign-in saved for ${res.data.user.zoho_email}. The connection agent ` +
          "will use it the next time a Zoho session is needed."
      );
    } catch (err: any) {
      setError(err.message || "Could not save the browser sign-in");
    } finally {
      setSavingLogin(false);
    }
  };

  const handleRemove = async (publicId: string) => {
    try {
      setError(null);
      await removeZohoUser(publicId);
      setNotice(
        "User removed and its credentials dropped. Also revoke the Self Client in " +
          "that account's Zoho API console to fully cut access."
      );
      setSelectedId(null);
      await fetchUsers();
    } catch (err: any) {
      setError(err.message || "Could not remove the Zoho user");
    }
  };

  const setField = (field: keyof typeof EMPTY_ADD_FORM) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setAddForm((prev) => ({ ...prev, [field]: e.target.value }));

  const addFormComplete =
    addForm.zoho_email.trim() !== "" &&
    addForm.client_id.trim() !== "" &&
    addForm.client_secret.trim() !== "" &&
    addForm.grant_code.trim() !== "";

  return (
    <div className="flex w-full flex-col items-start gap-6 p-8">
      {/* Heading */}
      <div className="flex w-full flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col items-start gap-1">
          <span className="text-heading-2 font-heading-2 text-default-font">
            Manage users
          </span>
          <span className="text-body font-body text-subtext-color">
            Zoho accounts the bot makes Books API calls as. Each carries up to{" "}
            {users[0]?.max_tenants ?? 10} tenants.
          </span>
        </div>
        <Button
          icon={<FeatherUserPlus />}
          onClick={() => {
            setShowAdd((v) => !v);
            setError(null);
          }}
        >
          {showAdd ? "Cancel" : "Add Zoho user"}
        </Button>
      </div>

      {error && (
        <div className="flex w-full items-start gap-2 rounded-md border border-solid border-error-200 bg-error-50 px-4 py-3">
          <FeatherAlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-error-700" />
          <span className="text-body font-body text-error-700">{error}</span>
        </div>
      )}
      {notice && (
        <div className="flex w-full items-start justify-between gap-2 rounded-md border border-solid border-success-200 bg-success-50 px-4 py-3">
          <span className="text-body font-body text-success-800">{notice}</span>
          <button
            className="text-caption font-caption text-success-800 underline"
            onClick={() => setNotice(null)}
          >
            dismiss
          </button>
        </div>
      )}

      {/* Add form — the grant code dies in minutes, so it's the LAST thing to
          generate: fill everything else first, then code, then submit. */}
      {showAdd && (
        <div className="flex w-full flex-col items-start gap-4 rounded-lg border border-solid border-neutral-border bg-default-background p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-100">
              <FeatherKey className="h-5 w-5 text-brand-600" />
            </div>
            <div className="flex flex-col items-start gap-1">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Add a Zoho user (Self Client)
              </span>
              <span className="text-body font-body text-subtext-color">
                Sign in to{" "}
                <a
                  className="text-brand-700 underline"
                  href={`https://${apiConsoleHost(addForm.dc)}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {apiConsoleHost(addForm.dc)}
                </a>{" "}
                as this Zoho account → Self Client → copy the Client ID/Secret →
                Generate Code with scope <code>{SELF_CLIENT_SCOPE}</code>. The code
                lasts only minutes — generate it last and submit straight away.
              </span>
            </div>
          </div>

          <div className="flex w-full flex-wrap items-end gap-3">
            <TextField className="h-auto grow shrink-0 basis-0" variant="outline" label="Zoho account email">
              <TextField.Input
                value={addForm.zoho_email}
                onChange={setField("zoho_email")}
                placeholder="zoho-bot1@zournalhq.com"
              />
            </TextField>
            <TextField className="h-auto grow shrink-0 basis-0" variant="outline" label="Label">
              <TextField.Input
                value={addForm.label}
                onChange={setField("label")}
                placeholder="Bot user 1"
              />
            </TextField>
            {/* Where this Zoho account lives. Decides which accounts host the
                grant code is exchanged at and which clients this bot can ever
                be invited by — it cannot be changed after connecting. */}
            <label className="flex h-auto grow shrink-0 basis-0 flex-col gap-1">
              <span className="text-caption-bold font-caption-bold text-default-font">
                Datacentre
              </span>
              <select
                value={addForm.dc}
                onChange={setField("dc")}
                className="h-10 w-full rounded-md border border-solid border-neutral-border bg-default-background px-3 text-body font-body text-default-font"
              >
                {DATACENTRES.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="flex w-full flex-wrap items-end gap-3">
            <TextField className="h-auto grow shrink-0 basis-0" variant="outline" label="Client ID">
              <TextField.Input
                value={addForm.client_id}
                onChange={setField("client_id")}
                placeholder="1000.XXXXXXXXXXXXXXXX"
              />
            </TextField>
            <TextField className="h-auto grow shrink-0 basis-0" variant="outline" label="Client secret">
              <TextField.Input
                type="password"
                value={addForm.client_secret}
                onChange={setField("client_secret")}
                placeholder="••••••••"
              />
            </TextField>
          </div>
          <div className="flex w-full flex-wrap items-end gap-3">
            <TextField
              className="h-auto grow shrink-0 basis-0"
              variant="outline"
              label="Grant code"
              helpText="From Self Client → Generate Code. Single-use, expires in 3–10 minutes."
            >
              <TextField.Input
                type="password"
                value={addForm.grant_code}
                onChange={setField("grant_code")}
                placeholder="1000.xxxxxxxx.xxxxxxxx"
              />
            </TextField>
            <Button
              icon={<FeatherCheckCircle />}
              loading={adding}
              disabled={!addFormComplete}
              onClick={handleAdd}
            >
              Connect user
            </Button>
          </div>
          <span className="text-caption font-caption text-subtext-color">
            Submitting exchanges the code with Zoho immediately and verifies the
            connection by listing the user's Books organisations. Secrets are stored
            encrypted and never shown again.
          </span>
        </div>
      )}

      {/* Users list */}
      <div className="flex w-full flex-col items-start overflow-hidden rounded-lg border border-solid border-neutral-border bg-default-background">
        <div className="flex w-full items-center justify-between gap-2 border-b border-solid border-neutral-border px-4 py-3">
          <span className="text-body-bold font-body-bold text-default-font">
            {users.length} Zoho user{users.length === 1 ? "" : "s"}
          </span>
          {loading && (
            <span className="text-caption font-caption text-subtext-color">loading…</span>
          )}
        </div>

        {users.length === 0 && !loading ? (
          <div className="flex w-full flex-col items-start gap-1 px-4 py-6">
            <span className="text-body font-body text-default-font">
              No Zoho users yet.
            </span>
            <span className="text-body font-body text-subtext-color">
              "Add Zoho user" connects the first account the bot will call Zoho Books
              as.
            </span>
          </div>
        ) : (
          <div className="w-full overflow-auto">
            <Table
              header={
                <Table.HeaderRow>
                  <Table.HeaderCell>User</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell>Organisations</Table.HeaderCell>
                  <Table.HeaderCell>Tenants</Table.HeaderCell>
                  <Table.HeaderCell>Last verified</Table.HeaderCell>
                </Table.HeaderRow>
              }
            >
              {users.map((user) => (
                <Table.Row
                  key={user.public_id}
                  clickable
                  onClick={() =>
                    setSelectedId((prev) =>
                      prev === user.public_id ? null : user.public_id
                    )
                  }
                  className={
                    selected?.public_id === user.public_id
                      ? "bg-brand-50"
                      : "cursor-pointer"
                  }
                >
                  <Table.Cell>
                    <div className="flex flex-col items-start">
                      <span className="text-body-bold font-body-bold text-default-font">
                        {user.label || user.zoho_email}
                      </span>
                      <span className="text-caption font-caption text-subtext-color">
                        {user.zoho_email}
                      </span>
                    </div>
                  </Table.Cell>
                  <Table.Cell>{statusBadge(user)}</Table.Cell>
                  <Table.Cell>
                    <span className="text-body font-body text-default-font">
                      {user.organizations.length}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="whitespace-nowrap text-body font-body text-default-font">
                      {user.tenant_count} / {user.max_tenants}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="whitespace-nowrap text-caption font-caption text-subtext-color">
                      {formatWhen(user.last_verified_at)}
                    </span>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table>
          </div>
        )}
      </div>

      {/* Detail */}
      {selected && (
        <div className="flex w-full flex-col items-start gap-4 rounded-lg border border-solid border-neutral-border bg-default-background p-6">
          <div className="flex w-full flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="text-heading-3 font-heading-3 text-default-font">
                {selected.label || selected.zoho_email}
              </span>
              {statusBadge(selected)}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="neutral-secondary"
                size="small"
                icon={<FeatherRefreshCw />}
                loading={testing}
                onClick={() => handleTest(selected.public_id)}
              >
                Test connection
              </Button>
              <Button
                variant="neutral-tertiary"
                size="small"
                icon={<FeatherKey />}
                onClick={() => setShowReconnect((v) => !v)}
              >
                {showReconnect ? "Cancel reconnect" : "Reconnect"}
              </Button>
              <Button
                variant="destructive-tertiary"
                size="small"
                onClick={() => handleRemove(selected.public_id)}
              >
                Remove
              </Button>
            </div>
          </div>

          <div className="flex w-full flex-col items-start gap-1">
            <span className="text-caption font-caption text-subtext-color">
              {selected.zoho_email} · {dcLabel(selected.dc)} · client{" "}
              {selected.client_id}
            </span>
            {/* Resolved by Zoho at connect time; the domain every Books call
                for this user goes to. Read-only — it belongs to the account. */}
            <span className="text-caption font-caption text-subtext-color">
              API domain {selected.api_domain ?? "not resolved yet"}
            </span>
            <span className="text-caption font-caption text-subtext-color">
              Scope {selected.granted_scopes || SELF_CLIENT_SCOPE} · verified{" "}
              {formatWhen(selected.last_verified_at)} · last used{" "}
              {formatWhen(selected.last_used_at)}
            </span>
            {selected.last_error && (
              <span className="text-caption font-caption text-error-700">
                {selected.last_error}
              </span>
            )}
          </div>

          {/* Reconnect — fresh grant code for a revoked/expired token. */}
          {showReconnect && (
            <div className="flex w-full flex-col items-start gap-3 rounded-lg border border-solid border-neutral-border bg-neutral-50 p-4">
              <span className="text-body font-body text-subtext-color">
                Generate a fresh code in{" "}
                <a
                  className="text-brand-700 underline"
                  href={`https://${apiConsoleHost(selected.dc)}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {apiConsoleHost(selected.dc)}
                  <FeatherExternalLink className="ml-0.5 inline h-3 w-3" />
                </a>{" "}
                (Self Client → Generate Code, scope <code>{SELF_CLIENT_SCOPE}</code>).
                Leave the secret blank to keep the stored one.
              </span>
              <div className="flex w-full flex-wrap items-end gap-3">
                <TextField className="h-auto grow shrink-0 basis-0" variant="outline" label="Grant code">
                  <TextField.Input
                    type="password"
                    value={reconnectCode}
                    onChange={(e) => setReconnectCode(e.target.value)}
                    placeholder="1000.xxxxxxxx.xxxxxxxx"
                  />
                </TextField>
                <TextField
                  className="h-auto grow shrink-0 basis-0"
                  variant="outline"
                  label="Client secret (only if it changed)"
                >
                  <TextField.Input
                    type="password"
                    value={reconnectSecret}
                    onChange={(e) => setReconnectSecret(e.target.value)}
                    placeholder="leave blank to keep stored"
                  />
                </TextField>
                <Button
                  loading={reconnecting}
                  disabled={!reconnectCode.trim()}
                  onClick={handleReconnect}
                >
                  Exchange & reconnect
                </Button>
              </div>
            </div>
          )}

          {/* Browser sign-in. Separate from the Self Client credentials above:
              those drive the API, this drives the headless browser that accepts
              organisation invitations, which Zoho has no API for. */}
          <div className="flex w-full flex-col items-start gap-3 rounded-lg border border-solid border-neutral-border p-4">
            <div className="flex w-full flex-wrap items-center justify-between gap-2">
              <span className="text-body-bold font-body-bold text-default-font">
                Browser sign-in
              </span>
              {selected.has_password ? (
                <Badge variant="success">Password stored</Badge>
              ) : (
                <Badge variant="warning">No password — invites can't be accepted</Badge>
              )}
            </div>
            <span className="text-body font-body text-subtext-color">
              Used only by the connection agent to accept Zoho Books invitations. Stored
              encrypted and never shown again. Set this account's second factor to
              <span className="font-body-bold"> email OTP</span> so the agent can read the
              code from the bot mailbox instead of needing a person.
            </span>
            <div className="flex w-full flex-wrap items-end gap-3">
              <TextField
                className="h-auto grow shrink-0 basis-0"
                variant="outline"
                label="Sign-in email"
              >
                <TextField.Input
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  placeholder={selected.zoho_email}
                />
              </TextField>
              <TextField
                className="h-auto grow shrink-0 basis-0"
                variant="outline"
                label="Zoho password"
              >
                <TextField.Input
                  type="password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  placeholder={
                    selected.has_password ? "leave blank to keep stored" : "not set yet"
                  }
                />
              </TextField>
              <Button
                variant="neutral-secondary"
                icon={<FeatherKey />}
                loading={savingLogin}
                disabled={!loginEmail.trim() && !loginPassword.trim()}
                onClick={handleSaveLogin}
              >
                Save sign-in
              </Button>
            </div>
          </div>

          {/* Organisations — what the token can actually reach. */}
          <div className="flex w-full flex-col items-start gap-2">
            <span className="text-body-bold font-body-bold text-default-font">
              Zoho Books organisations
            </span>
            {selected.organizations.length === 0 ? (
              <span className="text-body font-body text-subtext-color">
                None cached — run "Test connection".
              </span>
            ) : (
              <div className="flex w-full flex-wrap items-center gap-2">
                {selected.organizations.map((org) => (
                  <div
                    key={org.organization_id}
                    className="flex items-center gap-2 rounded-md border border-solid border-neutral-border bg-neutral-50 px-3 py-1.5"
                  >
                    <span className="text-body font-body text-default-font">
                      {org.name}
                    </span>
                    <span className="text-caption font-caption text-subtext-color">
                      #{org.organization_id}
                      {org.currency_code ? ` · ${org.currency_code}` : ""}
                    </span>
                    {org.is_default_org && <Badge variant="neutral">default</Badge>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Tenants — capped, assignment UI lands with the tenant-mapping phase. */}
          <div className="flex w-full flex-col items-start gap-2">
            <span className="text-body-bold font-body-bold text-default-font">
              Tenants ({selected.tenant_count} / {selected.max_tenants})
            </span>
            {tenants.length === 0 ? (
              <span className="text-body font-body text-subtext-color">
                No tenants assigned yet — assignment comes with the tenant-mapping
                phase.
              </span>
            ) : (
              <div className="flex w-full flex-wrap items-center gap-2">
                {tenants.map((t) => (
                  <div
                    key={t.tenant_id}
                    className="flex items-center gap-2 rounded-md border border-solid border-neutral-border bg-neutral-50 px-3 py-1.5"
                  >
                    <span className="text-body font-body text-default-font">
                      {t.company_name || `Company #${t.tenant_id}`}
                    </span>
                    {t.zoho_organization_id && (
                      <span className="text-caption font-caption text-subtext-color">
                        org #{t.zoho_organization_id}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BotZohoUsersPage;
